import { config } from 'app/config';

export interface FetchDataRequestParams<RequestBody> {
  method: string;
  url: string;
  headers?: HeadersInit;
  body?: RequestBody;
}

export interface FetchDataResponse<ResponseBody> {
  statusCode: number;
  body?: ResponseBody;
}

export interface ApiError {
  error?: string;
  detail?: string;
  status?: number;
  message?: string;
}

export class FetchDataError extends Error {
  statusCode: number;
  apiError?: ApiError;

  constructor({ statusCode, error }: { statusCode: number; error: ApiError }) {
    super(`FetchDataError: ${statusCode} | ${error?.message}`);
    this.apiError = error;
    this.statusCode = statusCode;
  }
}

const BASE_URL = config.apiGateway.URL;

export async function fetchData<T>(
  method: string,
  url: string,
  headers: HeadersInit,
  body?: any,
  isFileRequest: boolean = false,
  defaultFileName?: string,
): Promise<FetchDataResponse<T>> {
  try {
    const requestOptions = {
      method,
      headers,
      body: prepareRequestBody(body, isFileRequest),
    };

    const response = await fetch(`${BASE_URL}/${url}`, requestOptions);
    return await handleResponse(response, isFileRequest, defaultFileName);
  } catch (err) {
    return handleFetchError(err);
  }
}

function prepareRequestBody(body: any, isFileRequest: boolean): any {
  if (isFileRequest && body?.file) {
    const formData = new FormData();
    formData.append('file', body.file);
    return formData;
  }
  return JSON.stringify(body);
}

async function handleResponse(
  response: Response,
  isFileRequest: boolean,
  defaultFileName?: string,
): Promise<FetchDataResponse<any>> {
  const contentType = response.headers.get('content-type');

  if (response.status === 204 || (contentType === null && response.ok)) {
    return { statusCode: 204, body: null };
  }

  if (contentType === null && !response.ok) {
    throw new FetchDataError({ statusCode: response.status, error: null });
  }

  if (
    contentType?.includes('application/json') ||
    contentType?.includes('application/problem+json')
  ) {
    return handleJsonResponse(response);
  } else if (isFileRequest) {
    return handleFileResponse(response, defaultFileName);
  }

  // content type no manejado
  throw new FetchDataError({
    statusCode: response.status,
    error: null,
  });
}

async function handleJsonResponse(response: Response): Promise<FetchDataResponse<any>> {
  const data = await response.json();
  if (response.status >= 400) {
    throw new FetchDataError({ statusCode: response.status, error: data });
  }
  return { statusCode: response.status, body: data };
}

async function handleFileResponse(
  response: Response,
  defaultFileName?: string,
): Promise<FetchDataResponse<any>> {
  const blob = await response.blob();

  if (response.status >= 400) {
    throw new FetchDataError({ statusCode: response.status, error: null });
  }

  const contentDisposition = response.headers.get('content-disposition');

  let fileName: string | undefined;

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match?.[1]) {
      fileName = match[1];
    }
  }

  return {
    statusCode: response.status,
    body: {
      file: blob,
      name: fileName ?? defaultFileName ?? 'download',
    },
  };
}

function handleFetchError(err: any): never {
  // Si ya es un FetchDataError (como el que lanzamos desde handleJsonResponse),
  // simplemente lo volvemos a lanzar para no envolverlo dos veces.
  if (err instanceof FetchDataError) {
    throw err;
  }
  const statusCode = err.code === 'ECONNREFUSED' ? 503 : (err.status ?? 500);
  throw new FetchDataError({ statusCode, error: err });
}
